import axios from 'axios';
import AuthService from './auth-header';
import {
  isValidRecordingUrl,
  pickS3RecordingUrl,
  pickVendorRecordingUrl,
} from '@/utils/call-recording-helpers';
import {
  slimListRow,
  extractCallAnalysisPayload,
  applyCallAnalysisDetailBody,
} from '@/utils/call-analysis-helpers';

export { extractCallAnalysisPayload, applyCallAnalysisDetailBody };

let API_URL = ''
if (process.env.NODE_ENV === "production") {
    API_URL = 'https://ats.sattvahuman.in/api/'
} else {
    API_URL = 'http://192.168.0.30:5000/api/'
}

const DEFAULT_PAGE = 1;
/** Sensible page size — do not request the SQL max (5000) up front. */
const DEFAULT_LIMIT = 50;
/** Hard cap per request — never drain the full org set client-side. */
const MAX_LIMIT = 100;
/** Abort hung call-analytics requests so UI loading flags can clear. */
const CALL_ANALYTICS_TIMEOUT_MS = 45000;
/** Live period drain (History / KPI / Team / Org / search / export). */
export const PERIOD_CACHE_TTL_MS = 5 * 60 * 1000;
/** API 2 (one sid) — fail fast. Do not inherit the 45s list timeout. */
const CALL_ANALYSIS_DETAIL_TIMEOUT_MS = 10000;
/** Presign / play-url — never leave the modal spinner hanging. */
const CALL_RECORDING_TIMEOUT_MS = 12000;

function toPositiveInt(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function messageFromBody(body) {
    if (!body) return '';
    if (typeof body === 'string') {
        const trimmed = body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        return trimmed.slice(0, 180);
    }
    if (typeof body === 'object') {
        return body.message || body.error || (body.data && body.data.message) || '';
    }
    return '';
}

/** HTTP 4xx/5xx is never success — even if a proxy body says status: OK. */
function failedFromHttp(err, fallback) {
    const httpStatus = err && err.response ? err.response.status : null;
    const apiMessage = messageFromBody(err && err.response ? err.response.data : null);
    let message = apiMessage || fallback || err.message || 'Request failed';
    if (httpStatus === 502 && !apiMessage) {
        message = 'Service is unavailable (HTTP 502). Please try again.';
    } else if (httpStatus === 404 && !apiMessage) {
        message = 'This endpoint was not found (HTTP 404).';
    } else if (httpStatus != null && !apiMessage) {
        message = `${fallback || 'Request failed'} (HTTP ${httpStatus}).`;
    }
    return {
        status: 'FAILED',
        httpStatus,
        message,
        data: { message },
    };
}

/**
 * True when an envelope count is a real number (including 0).
 * Missing, blank, or boolean placeholders are unused — do not coerce to 0.
 */
export function isPresentKpiCount(value) {
    if (value == null || value === '') return false;
    if (typeof value === 'boolean') return false;
    const n = Number(value);
    return Number.isFinite(n) && n >= 0;
}

/**
 * KPI totals from API 1 (`counts` / `summary` / `kpis`). Counts only — not rows.
 * Present when backend ships total_calls / connected / not_answered / failed /
 * missed / avg_duration_seconds. 0 is kept only when the key is actually sent;
 * undefined / unused stubs are omitted so the UI can drain rows instead.
 * Not Answered / Missed badges still prefer the painted widget list length
 * when that list is non-empty (envelope 0 is ignored).
 *
 * @returns {object|null}
 */
export function extractKpiSummaryCounts(body) {
    if (!body || typeof body !== 'object') return null;

    const bags = [];
    if (body.counts && typeof body.counts === 'object' && !Array.isArray(body.counts)) {
        bags.push(body.counts);
    }
    if (body.summary && typeof body.summary === 'object' && !Array.isArray(body.summary)) {
        bags.push(body.summary);
    }
    if (body.kpis && typeof body.kpis === 'object' && !Array.isArray(body.kpis)) {
        bags.push(body.kpis);
    }
    if (body.kpi && typeof body.kpi === 'object' && !Array.isArray(body.kpi)) {
        bags.push(body.kpi);
    }
    if (body.meta && body.meta.counts && typeof body.meta.counts === 'object') {
        bags.push(body.meta.counts);
    }
    if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
        if (body.data.counts && typeof body.data.counts === 'object') bags.push(body.data.counts);
        if (body.data.summary && typeof body.data.summary === 'object') bags.push(body.data.summary);
    }

    const fields = {
        total_calls: ['total_calls', 'totalCalls'],
        connected: ['connected', 'connected_count', 'connectedCount'],
        not_answered: ['not_answered', 'notAnswered', 'not_answered_count', 'notAnsweredCount'],
        failed: ['failed', 'failed_count', 'failedCount'],
        missed: ['missed', 'missed_count', 'missedCount', 'missed_calls'],
        avg_duration_seconds: [
            'avg_duration_seconds', 'avgDurationSeconds', 'avg_duration', 'avgDuration',
        ],
    };

    const out = {};
    bags.forEach((bag) => {
        Object.keys(fields).forEach((canon) => {
            if (out[canon] != null) return;
            const aliases = fields[canon];
            for (let i = 0; i < aliases.length; i += 1) {
                const raw = bag[aliases[i]];
                if (!isPresentKpiCount(raw)) continue;
                out[canon] = Number(raw);
                break;
            }
        });
    });

    // Unused stub: total > 0 but every status bucket is 0 / absent.
    // Drop those zeros so widgets drain/compute from slim rows instead.
    if (
        out.total_calls > 0
        && (out.connected == null || out.connected === 0)
        && (out.not_answered == null || out.not_answered === 0)
        && (out.failed == null || out.failed === 0)
        && (out.missed == null || out.missed === 0)
    ) {
        delete out.connected;
        delete out.not_answered;
        delete out.failed;
        delete out.missed;
    }

    const hasBreakdown = out.connected != null
        || out.not_answered != null
        || out.failed != null
        || out.missed != null
        || out.avg_duration_seconds != null;
    return hasBreakdown ? out : null;
}

/**
 * Normalize heterogeneous call-analytics payloads to `{ data, totalRecords }`.
 * Total may live on the root, under `pagination`/`meta`, nested under `data`,
 * or echoed as `total_count` on each row.
 * KPI breakdown (when present) is `kpiCounts` — never derived from this page's rows.
 */
export function normalizeCallAnalyticsResponse(body) {
    if (!body || typeof body !== 'object') {
        return { status: 'FAILED', data: [], totalRecords: 0 };
    }

    let rows = [];
    if (Array.isArray(body.data)) {
        rows = body.data;
    } else if (Array.isArray(body.records)) {
        rows = body.records;
    } else if (body.data && typeof body.data === 'object') {
        if (Array.isArray(body.data.data)) rows = body.data.data;
        else if (Array.isArray(body.data.rows)) rows = body.data.rows;
        else if (Array.isArray(body.data.records)) rows = body.data.records;
    }

    // Prefer root/pagination totals — do not MAX with per-row total_count echoes (can inflate).
    const rootTotalCandidates = [
        body.totalRecords,
        body.total_count,
        body.total,
        body.totalCount,
        body.pagination?.totalRecords,
        body.pagination?.total_count,
        body.pagination?.total,
        body.meta?.totalRecords,
        body.meta?.total,
        body.data?.totalRecords,
        body.data?.total_count,
        body.data?.total,
        body.data?.pagination?.totalRecords,
        body.data?.pagination?.total,
    ];

    let totalRecords = 0;
    for (let i = 0; i < rootTotalCandidates.length; i += 1) {
        const n = toPositiveInt(rootTotalCandidates[i]);
        if (n > 0) {
            totalRecords = n;
            break;
        }
    }
    if (!totalRecords) {
        totalRecords = toPositiveInt(rows[0]?.total_count) || toPositiveInt(rows[0]?.totalRecords);
    }
    if (!totalRecords) totalRecords = rows.length;

    const kpiCounts = extractKpiSummaryCounts(body);

    return {
        ...body,
        data: rows,
        totalRecords,
        kpiCounts,
    };
}

/**
 * Query parameters for GET /api/exotel/call-analytics.
 *
 * @typedef {Object} CallAnalyticsParams
 * @property {string} startDate - Start date (YYYY-MM-DD), inclusive.
 * @property {string} endDate - End date (YYYY-MM-DD), inclusive.
 * @property {string} [emp_id] - Recruiter employee ID (e.g. "SH-230").
 * @property {string} [mgr_id] - Manager employee ID (e.g. "SH-072").
 * @property {number} [page=1] - Page number (1-based).
 * @property {number} [limit=50] - Page size (capped at 100).
 * @property {number} [offset] - Optional row offset; sent as `(page-1)*limit` when omitted.
 * @property {boolean} [analyzed] - When true/false, request only analyzed / not-analyzed rows.
 *   Backend MUST filter by completed call_analysis and return a matching totalRecords.
 *   If ignored, History shows the page as returned — it does not client-filter and
 *   keep an unfiltered total.
 * @property {string} [vertical] - Job vertical (Service Industry / Infra Industry). Sent if
 *   the list API accepts it; production get_enriched_call_logs currently ignores it.
 * @property {string} [category] - Job category (White Collar / Blue Collar). Same as vertical.
 * @property {string} [client] - Client display name. Sent if the list API accepts it;
 *   production get_enriched_call_logs currently ignores it — UI still filters live rows.
 * @property {string} [client_name] - Alias for client (same ignore/local-filter rule).
 * @property {string|number} [client_id] - Client id when the master list has one.
 * @property {string} [sort='created_at'] - Requested sort column. Prod currently ignores this
 *   and already returns newest first; client still sends desc and re-sorts the page.
 * @property {string} [order='desc'] - Requested sort direction (desc = latest first).
 */

/**
 * Single enriched call log row from SQL function get_enriched_call_logs.
 *
 * @typedef {Object} EnrichedCallLogRow
 * @property {number|string} id
 * @property {string} sid
 * @property {string} created_at - Canonical list timestamp (also createdAt / call_time / start_time / StartTime / dateCreated / call_date / updated_at)
 * @property {string} date_created
 * @property {string} direction
 * @property {string} status
 * @property {number|string} duration
 * @property {string} to_number
 * @property {string} from_number
 * @property {string} recruiter
 * @property {number|string|null} candidate_id
 * @property {number|string|null} job_id
 * @property {string|null} recording_s3_key
 * @property {string|null} [recording_url]
 * @property {Object|null} call_analysis - Badge-only meta (API 1). Full payload is API 2.
 * @property {string|null} call_analysis_status
 * @property {string|null} call_analysis_at
 * @property {string|null} candidate_name
 * @property {string|null} candidate_contact_no
 * @property {string|null} candidate_status
 * @property {Object|string|null} pipeline_notes
 * @property {string|null} [share_reason]
 * @property {boolean} [calls_available_skip]
 * @property {string|null} [job_vertical]
 * @property {string|null} [job_category]
 * @property {string|null} position_name
 * @property {string|null} client_name
 * @property {string|null} recruiter_name
 * @property {number} [total_count] - Total row count echoed on each row.
 */

/**
 * Applied filter values echoed by the API.
 *
 * @typedef {Object} CallAnalyticsFilters
 * @property {string} p_start_date
 * @property {string} p_end_date
 * @property {string} [_emp_id]
 * @property {string} [_mgr_id]
 */

/**
 * Successful response from GET /api/exotel/call-analytics.
 *
 * @typedef {Object} CallAnalyticsResponse
 * @property {'OK'} status
 * @property {EnrichedCallLogRow[]} data
 * @property {number} totalRecords
 * @property {object|null} [kpiCounts] - API 1 envelope counts when present
 * @property {CallAnalyticsFilters} filters
 */

const SESSION_SUMMARY_UNAVAILABLE = 'callAnalytics.analysisSummaryUnavailable';
const SESSION_DETAIL_UNAVAILABLE = 'callAnalytics.analysisDetailUnavailable';

function readSessionFlag(key) {
    try {
        return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key) === '1';
    } catch (e) {
        return false;
    }
}

function markSessionFlag(key) {
    try {
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, '1');
    } catch (e) { /* private mode / quota */ }
}

function looksLikeHtmlOrMissingRouteBody(data) {
    if (data == null) return true;
    if (typeof data === 'string') {
        return /cannot get|not found|<!doctype|<html/i.test(data);
    }
    if (typeof data !== 'object') return true;
    const msg = String(data.message || data.error || '').toLowerCase();
    return /not implemented|unknown (route|endpoint)|cannot get|no such route/.test(msg);
}

/** Collection 404/501/405 = route not deployed. */
function isCollectionNotImplemented(err) {
    const http = err && err.response ? err.response.status : null;
    if (http === 501 || http === 405) return true;
    if (http === 404) return true;
    return false;
}

/** Detail 404 is ambiguous (missing route vs no analysis for this sid). */
function isDetailRouteMissing(err) {
    const http = err && err.response ? err.response.status : null;
    if (http === 501 || http === 405) return true;
    if (http !== 404) return false;
    return looksLikeHtmlOrMissingRouteBody(err.response && err.response.data);
}

function buildAnalyticsQuery(params = {}) {
    const {
        startDate,
        endDate,
        emp_id,
        mgr_id,
        page = DEFAULT_PAGE,
        limit = DEFAULT_LIMIT,
        offset,
        analyzed,
        vertical,
        category,
        client,
        client_name,
        client_id,
        sort,
        order,
    } = params;

    const query = {};
    if (startDate != null) query.startDate = startDate;
    if (endDate != null) query.endDate = endDate;
    if (emp_id != null) query.emp_id = emp_id;
    if (mgr_id != null) query.mgr_id = mgr_id;
    if (vertical != null && vertical !== '' && vertical !== 'all') query.vertical = vertical;
    if (category != null && category !== '' && category !== 'all') query.category = category;
    if (client != null && client !== '' && client !== 'all') query.client = client;
    if (client_name != null && client_name !== '' && client_name !== 'all') query.client_name = client_name;
    if (client_id != null && client_id !== '' && client_id !== 'all') query.client_id = client_id;
    query.sort = sort || 'created_at';
    query.order = order === 'asc' ? 'asc' : 'desc';
    const pageNum = page != null ? toPositiveInt(page) || DEFAULT_PAGE : DEFAULT_PAGE;
    const limitNum = Math.min(
        Math.max(1, Number(limit) || DEFAULT_LIMIT),
        MAX_LIMIT,
    );
    query.page = pageNum;
    query.limit = limitNum;
    query.offset = offset != null
        ? Math.max(0, Math.floor(Number(offset)) || 0)
        : (pageNum - 1) * limitNum;
    if (analyzed === true || analyzed === false) {
        query.analyzed = analyzed;
    }
    return query;
}

function listRequestHeaders() {
    return {
        ...AuthService.authHeader(),
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
    };
}

function cancelledResult() {
    return {
        status: 'CANCELLED',
        message: 'cancelled',
        data: [],
        totalRecords: 0,
    };
}

function timeoutResult() {
    return {
        status: 'FAILED',
        message: 'Call analytics request timed out. Try a smaller date range or page.',
        data: { message: 'Call analytics request timed out. Try a smaller date range or page.' },
    };
}

function detailTimeoutResult() {
    return {
        status: 'TIMEOUT',
        message: 'Analysis is taking too long. Please try again.',
        data: null,
    };
}

function unavailableResult(message) {
    return {
        status: 'UNAVAILABLE',
        message,
        data: [],
        totalRecords: 0,
    };
}

class CallAnalyticsService {
    constructor() {
        this._summaryUnavailable = readSessionFlag(SESSION_SUMMARY_UNAVAILABLE);
        this._detailUnavailable = readSessionFlag(SESSION_DETAIL_UNAVAILABLE);
        /** @type {Promise<any>|null} */
        this._summaryProbe = null;
        /** @type {Map<string, object|null>} sid/id → analysis object (OK only; misses are not stored) */
        this._detailCache = new Map();
        /** @type {Map<string, Promise<any>>} */
        this._detailInflight = new Map();
        /**
         * In-memory period drain, keyed by filter scope.
         * @type {Map<string, { rows: object[], total: number, complete: boolean, summaryCounts: object|null, fetchedAt: number }>}
         */
        this._periodListCache = new Map();
    }

    _markSummaryUnavailable() {
        this._summaryUnavailable = true;
        markSessionFlag(SESSION_SUMMARY_UNAVAILABLE);
    }

    _markDetailUnavailable() {
        this._detailUnavailable = true;
        markSessionFlag(SESSION_DETAIL_UNAVAILABLE);
    }

    /** Drop in-memory analysis payloads so the next visit refetches. */
    clearDetailCache() {
        this._detailCache.clear();
        this._detailInflight.clear();
    }

    _prunePeriodListCache() {
        const now = Date.now();
        this._periodListCache.forEach((entry, key) => {
            if (!entry || now - entry.fetchedAt >= PERIOD_CACHE_TTL_MS) {
                this._periodListCache.delete(key);
            }
        });
    }

    /**
     * Complete period drain for this scope, if still within the 5-minute TTL.
     * @param {string} key
     * @returns {object|null}
     */
    getPeriodListCache(key) {
        if (!key) return null;
        this._prunePeriodListCache();
        const entry = this._periodListCache.get(key);
        if (!entry || !entry.complete || !Array.isArray(entry.rows)) return null;
        if (Date.now() - entry.fetchedAt >= PERIOD_CACHE_TTL_MS) {
            this._periodListCache.delete(key);
            return null;
        }
        return entry;
    }

    /**
     * Store a finished period drain. Incomplete pages are not cached.
     * @param {string} key
     * @param {{ rows: object[], total?: number, summaryCounts?: object|null }} payload
     */
    setPeriodListCache(key, payload) {
        if (!key || !payload || !Array.isArray(payload.rows)) return;
        this._prunePeriodListCache();
        this._periodListCache.set(key, {
            rows: payload.rows,
            total: Number(payload.total) || payload.rows.length,
            complete: true,
            summaryCounts: payload.summaryCounts || null,
            fetchedAt: Date.now(),
        });
    }

    /** Drop one scope (Refresh / expiry) or every scope (logout). */
    invalidatePeriodListCache(key) {
        if (key) this._periodListCache.delete(key);
        else this._periodListCache.clear();
    }

    clearPeriodListCache() {
        this._periodListCache.clear();
    }

    /** Logout / session expiry — drop period drain + analysis detail. */
    clearSessionCaches() {
        this.clearDetailCache();
        this.clearPeriodListCache();
    }

    /**
     * Presigned S3 URL for a recording key (mirrors JobService.getFile).
     * @param {string} s3Key
     * @returns {Promise<{ status: string, url?: string, file?: string, message?: string }>}
     */
    async getRecordingFileUrl(s3Key) {
        if (!s3Key) {
            return { status: 'FAILED', message: 'Missing S3 key' };
        }
        try {
            const encodedKey = encodeURIComponent(s3Key);
            const res = await axios.get(`${API_URL}exotel/recording/file/${encodedKey}`, {
                headers: AuthService.authHeader(),
                timeout: CALL_RECORDING_TIMEOUT_MS,
            });
            const body = res.data;
            const url = body?.url || body?.file;
            if (body?.status === 'OK' && url) {
                return { status: 'OK', url, source: body.source || 's3_presigned' };
            }
            return body || { status: 'FAILED', message: 'Could not load recording from S3' };
        } catch (err) {
            if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
                return { status: 'TIMEOUT', message: 'Recording is taking too long. Please try again.' };
            }
            if (err.response) return failedFromHttp(err, 'Could not load recording from S3');
            return { status: 'FAILED', message: err.message || 'Request failed' };
        }
    }

    /**
     * Base64 recording bytes for download (mirrors JobService.getFileBase64).
     * @param {string} s3Key
     * @returns {Promise<{ status: string, file?: string, message?: string }>}
     */
    async getRecordingFileBase64(s3Key) {
        if (!s3Key) {
            return { status: 'FAILED', message: 'Missing S3 key' };
        }
        try {
            const encodedKey = encodeURIComponent(s3Key);
            const res = await axios.get(`${API_URL}exotel/recording/file/base64/${encodedKey}`, {
                headers: AuthService.authHeader(),
            });
            const body = res.data;
            if (body?.status === 'OK' && body.file) {
                return { status: 'OK', file: body.file, source: body.source || 's3_base64' };
            }
            return body || { status: 'FAILED', message: 'Could not download recording from S3' };
        } catch (err) {
            if (err.response) return failedFromHttp(err, 'Could not download recording from S3');
            return { status: 'FAILED', message: err.message || 'Request failed' };
        }
    }

    /**
     * Resolve play URL: S3 key first (like CV), then one play-url lookup, vendor URL last.
     * One HTTP call per action — no /calls/:id or /calls/sid/:sid alias probing.
     * @param {{ recordingS3Key?: string, sid?: string, callDbId?: number|string, item?: object }} params
     */
    async resolveRecordingPlayUrl(params = {}) {
        const {
            recordingS3Key,
            sid,
            callDbId,
            item = {},
        } = params;

        const s3Key = recordingS3Key || item.recordingS3Key || null;

        if (s3Key) {
            return this.getRecordingFileUrl(s3Key);
        }

        const cachedS3Url = pickS3RecordingUrl(item);
        if (cachedS3Url) {
            return { status: 'OK', url: cachedS3Url, source: 'stored_s3_url' };
        }

        if (sid || callDbId != null) {
            return this._resolveRecordingPlayUrlByCallId({ sid, callDbId });
        }

        const vendorUrl = pickVendorRecordingUrl(item);
        if (vendorUrl) {
            return { status: 'OK', url: vendorUrl, source: 'vendor' };
        }

        return { status: 'FAILED', message: 'No recording available for this call.' };
    }

    /**
     * Resolve download: S3 base64 when key exists (like CV download), else play URL.
     */
    async resolveRecordingDownload(params = {}) {
        const s3Key = params.recordingS3Key || params.item?.recordingS3Key || null;
        if (s3Key) {
            return this.getRecordingFileBase64(s3Key);
        }
        return this.resolveRecordingPlayUrl(params);
    }

    async _resolveRecordingPlayUrlByCallId(params = {}) {
        const { sid, callDbId } = params;
        const query = {};
        if (sid) query.sid = sid;
        if (callDbId != null) query.callId = callDbId;

        if (!query.sid && query.callId == null) {
            return { status: 'FAILED', message: 'Missing call identifier' };
        }

        try {
            const res = await axios.get(API_URL + 'exotel/calls/recording-play-url', {
                params: query,
                headers: AuthService.authHeader(),
                timeout: CALL_RECORDING_TIMEOUT_MS,
            });
            const body = res.data;
            if (body?.status === 'OK' && isValidRecordingUrl(body.url)) {
                return { status: 'OK', url: body.url, source: body.source || null };
            }
            if (body?.status === 'OK' && body.url) {
                return { status: 'FAILED', message: 'Recording URL is not playable.', source: body.source || null };
            }
            return body || { status: 'FAILED', message: 'No recording available' };
        } catch (err) {
            if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
                return { status: 'TIMEOUT', message: 'Recording is taking too long. Please try again.' };
            }
            if (err.response) {
                return failedFromHttp(err, 'No recording available for this call.');
            }
            return { status: 'FAILED', message: err.message || 'Request failed' };
        }
    }

    async getCallAnalytics(params = {}) {
        const query = buildAnalyticsQuery(params);

        try {
            const res = await axios.get(API_URL + 'exotel/call-analytics', {
                params: query,
                headers: listRequestHeaders(),
                timeout: CALL_ANALYTICS_TIMEOUT_MS,
                cancelToken: params.cancelToken,
            });
            const body = res.data;
            if (!body || body.status !== 'OK') {
                const message = messageFromBody(body) || 'Call analytics did not return a successful response.';
                return {
                    status: 'FAILED',
                    message,
                    data: { message },
                };
            }
            return normalizeCallAnalyticsResponse(body);
        } catch (err) {
            if (axios.isCancel && axios.isCancel(err)) return cancelledResult();
            if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
                return timeoutResult();
            }
            if (err.response) {
                return failedFromHttp(err, 'Could not load call analytics.');
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error loading call analytics.',
                data: { message: err.message || 'Network error loading call analytics.' },
            };
        }
    }

    /**
     * API 1 — list rows: call data + analysis metadata / badges only.
     * GET /api/exotel/call-analytics/analysis-summary
     * Probe once per session — 404/501/405 marks the route unavailable
     * (prod today). Fat-list fallback lives in getPeriodAnalytics and goes
     * away after this route is deployed.
     */
    async getAnalysisSummary(params = {}) {
        if (this._summaryUnavailable) {
            return unavailableResult('Call analysis summary endpoint is not available.');
        }

        if (this._summaryProbe) {
            await this._summaryProbe;
            if (this._summaryUnavailable) {
                return unavailableResult('Call analysis summary endpoint is not available.');
            }
            return this._requestAnalysisSummary(params);
        }

        const request = this._requestAnalysisSummary(params);
        this._summaryProbe = request.finally(() => {
            this._summaryProbe = null;
        });
        return request;
    }

    async _requestAnalysisSummary(params = {}) {
        try {
            const res = await axios.get(API_URL + 'exotel/call-analytics/analysis-summary', {
                params: buildAnalyticsQuery(params),
                headers: listRequestHeaders(),
                timeout: CALL_ANALYTICS_TIMEOUT_MS,
                cancelToken: params.cancelToken,
            });
            const body = res.data;
            if (!body || body.status !== 'OK') {
                const message = messageFromBody(body) || 'Analysis summary did not return a successful response.';
                if (looksLikeHtmlOrMissingRouteBody(body)) {
                    this._markSummaryUnavailable();
                    return unavailableResult(message);
                }
                return {
                    status: 'FAILED',
                    message,
                    data: { message },
                };
            }
            const normalized = normalizeCallAnalyticsResponse(body);
            const rows = Array.isArray(normalized.data)
                ? normalized.data.map(slimListRow)
                : [];
            return {
                ...normalized,
                data: rows,
                source: 'analysis-summary',
            };
        } catch (err) {
            if (axios.isCancel && axios.isCancel(err)) return cancelledResult();
            if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
                return timeoutResult();
            }
            if (isCollectionNotImplemented(err)) {
                this._markSummaryUnavailable();
                return unavailableResult('Call analysis summary endpoint is not available.');
            }
            if (err.response) {
                return failedFromHttp(err, 'Could not load analysis summary.');
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error loading analysis summary.',
                data: { message: err.message || 'Network error loading analysis summary.' },
            };
        }
    }

    /**
     * API 1 for every Call Analytics list surface (History, Missed, Team,
     * Datewise, Hourly, KPIs). Never request the fat list just to draw the
     * table when analysis-summary exists.
     *
     * Prod today 404s analysis-summary — fall back to GET /call-analytics
     * (transcripts stripped). That fallback goes away after deploy.
     */
    async getPeriodAnalytics(params = {}) {
        const summary = await this.getAnalysisSummary(params);
        if (summary.status === 'OK' || summary.status === 'CANCELLED') return summary;
        if (summary.status !== 'UNAVAILABLE') return summary;

        const list = await this.getCallAnalytics(params);
        if (list.status !== 'OK') return list;
        const rows = Array.isArray(list.data) ? list.data.map(slimListRow) : [];
        return {
            ...list,
            data: rows,
            source: 'call-analytics-fallback',
        };
    }

    /**
     * API 2 — modal-field analysis for one call. Client calls this only on
     * View Analysis / row click. Not used to paint list / KPI / Missed.
     * GET /api/exotel/call-analytics/:sid/analysis — uses :id when sid is missing.
     * Remembered per sid for this visit only (cleared on leave).
     * Missing-route 404/501 is probed once per session.
     * Independent of API 1 — a summary 404 must not skip this request.
     * Response is allowlisted (slimAnalysisDetail) — no transcript / raw_payload.
     * Never falls back to fat GET /call-analytics (sid/id query is ignored;
     * walking 3000+ rows would hang the modal).
     */
    async getCallAnalysisDetail(params = {}) {
        const sid = params.sid ? String(params.sid).trim() : '';
        const callDbId = params.callDbId != null && params.callDbId !== ''
            ? String(params.callDbId)
            : '';
        const pathId = sid || callDbId;
        if (!pathId) {
            return { status: 'FAILED', message: 'Missing call identifier' };
        }

        const cached = this._cachedDetail(pathId);
        const cachedAlt = sid && callDbId && sid !== callDbId
            ? this._cachedDetail(callDbId)
            : null;
        if (cached && cached.status === 'OK') return cached;
        if (cachedAlt && cachedAlt.status === 'OK') return cachedAlt;

        if (this._detailUnavailable) {
            return unavailableResult('Call analysis detail endpoint is not available.');
        }

        const primary = await this._requestCallAnalysisDetail(pathId, params.cancelToken);
        if (primary.status === 'OK' && primary.data && sid && callDbId && sid !== callDbId) {
            this._detailCache.set(sid, primary.data);
            this._detailCache.set(callDbId, primary.data);
        }
        if (
            primary.status === 'FAILED'
            && primary.httpStatus === 404
            && sid
            && callDbId
            && callDbId !== pathId
        ) {
            const cachedId = this._cachedDetail(callDbId);
            if (cachedId) return cachedId;
            const retry = await this._requestCallAnalysisDetail(callDbId, params.cancelToken);
            if (retry.status === 'OK' && retry.data) {
                this._detailCache.set(sid, retry.data);
                this._detailCache.set(callDbId, retry.data);
            }
            return retry;
        }
        return primary;
    }

    _cachedDetail(key) {
        if (!key || !this._detailCache.has(key)) return null;
        const cached = this._detailCache.get(key);
        if (cached) {
            return { status: 'OK', data: cached, source: 'memory' };
        }
        return null;
    }

    async _requestCallAnalysisDetail(pathId, cancelToken) {
        const inflightKey = String(pathId);
        if (this._detailInflight.has(inflightKey)) {
            return this._detailInflight.get(inflightKey);
        }
        const request = this._fetchCallAnalysisDetail(pathId, cancelToken);
        this._detailInflight.set(inflightKey, request);
        try {
            return await request;
        } finally {
            this._detailInflight.delete(inflightKey);
        }
    }

    async _fetchCallAnalysisDetail(pathId, cancelToken) {
        try {
            const res = await axios.get(
                `${API_URL}exotel/call-analytics/${encodeURIComponent(pathId)}/analysis`,
                {
                    headers: listRequestHeaders(),
                    timeout: CALL_ANALYSIS_DETAIL_TIMEOUT_MS,
                    cancelToken,
                },
            );
            const body = res.data;
            if (!body || body.status !== 'OK') {
                const message = messageFromBody(body) || 'Call analysis did not return a successful response.';
                if (looksLikeHtmlOrMissingRouteBody(body)) {
                    this._markDetailUnavailable();
                    return unavailableResult(message);
                }
                return { status: 'FAILED', message, data: null };
            }
            const analysis = applyCallAnalysisDetailBody(body);
            if (!analysis) {
                return { status: 'FAILED', message: 'No analysis payload in response.', data: null };
            }
            this._detailCache.set(pathId, analysis);
            return { status: 'OK', data: analysis, source: 'analysis-detail' };
        } catch (err) {
            if (axios.isCancel && axios.isCancel(err)) {
                return { status: 'CANCELLED', message: 'cancelled', data: null };
            }
            if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
                return detailTimeoutResult();
            }
            // JSON 404 = no analysis for this sid. Do not treat as "route missing"
            // (isCollectionNotImplemented is 404-any — that would skip API 2 all session).
            if (isDetailRouteMissing(err)) {
                this._markDetailUnavailable();
                return unavailableResult('Call analysis detail endpoint is not available.');
            }
            const http = err && err.response ? err.response.status : null;
            if (http === 404) {
                return {
                    status: 'FAILED',
                    httpStatus: 404,
                    message: 'No analysis for this call.',
                    data: null,
                };
            }
            if (err.response) {
                return failedFromHttp(err, 'Could not load call analysis.');
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error loading call analysis.',
                data: null,
            };
        }
    }
}

export default new CallAnalyticsService();
